// src/pages/BCCManager/components/AddUserModal.jsx

import React, { useState, useEffect } from "react";

const EMPTY_FORM = { name: "", email: "" };

export default function AddUserModal({ user, onSave, onClose }) {
    const [form, setForm]         = useState(EMPTY_FORM);
    const [errors, setErrors]     = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        setForm(user ? { id: user.id, name: user.name, email: user.email } : EMPTY_FORM);
        setErrors({});
    }, [user]);

    // ── Validation ─────────────────────────────────────────────────────────────
    const validate = () => {
        const errs = {};
        if (!form.name.trim())
            errs.name = "Name is required.";
        if (!form.email.trim())
            errs.email = "Email is required.";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
            errs.email = "Enter a valid email address.";
        return errs;
    };

    // ── Handlers ───────────────────────────────────────────────────────────────
    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev)   => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: ""    }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errs = validate();
        if (Object.keys(errs).length > 0) { setErrors(errs); return; }
        setIsSubmitting(true);
        await onSave(form);  // API call happens in parent (index.jsx)
        setIsSubmitting(false);
    };

    // ── Render ──────────────────────────────────────────────────────────────────
    return (
        <div
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-white dark:bg-navy-800 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden border border-gray-200 dark:border-navy-700"
            >
                {/* Header */}
                <div
                    className="flex items-center justify-between px-6 py-4"
                    style={{ background: "#004c86" }}
                >
                    <h2 className="text-base font-bold text-white m-0">
                        {user ? "✏️ Edit Recipient" : " Add New Recipient"}
                    </h2>
                    <button
                        onClick={onClose}
                        className="bg-white/20 hover:bg-white/35 text-white border-none w-7 h-7 rounded-full cursor-pointer text-sm flex items-center justify-center transition-all duration-200"
                    >
                        ✕
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6">

                    {/* Full Name */}
                    <div className="mb-5">
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                            Full Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                            placeholder="Name of the user"
                            disabled={isSubmitting}
                            style={{
                                "--tw-ring-color": "#004c86",
                            }}
                            className={`w-full px-3 py-2.5 border rounded-lg text-sm bg-white dark:bg-navy-900 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 outline-none transition-all duration-200
                        focus:ring-2 focus:border-[#004c86] disabled:opacity-50 disabled:cursor-not-allowed
                        ${errors.name
                                ? "border-red-500 dark:border-red-400"
                                : "border-gray-200 dark:border-navy-600"
                            }`}
                            onFocus={(e) => e.target.style.boxShadow = "0 0 0 2px #004c8640"}
                            onBlur={(e) => e.target.style.boxShadow = "none"}
                        />
                        {errors.name && (
                            <span className="block text-red-500 dark:text-red-400 text-xs mt-1">
                        {errors.name}
                    </span>
                        )}
                    </div>

                    {/* Email */}
                    <div className="mb-5">
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                            Email Address <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="email"
                            name="email"
                            value={form.email}
                            onChange={handleChange}
                            placeholder="Email Address"
                            disabled={isSubmitting}
                            className={`w-full px-3 py-2.5 border rounded-lg text-sm bg-white dark:bg-navy-900 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 outline-none transition-all duration-200
                        focus:ring-2 focus:border-[#004c86] disabled:opacity-50 disabled:cursor-not-allowed
                        ${errors.email
                                ? "border-red-500 dark:border-red-400"
                                : "border-gray-200 dark:border-navy-600"
                            }`}
                            onFocus={(e) => e.target.style.boxShadow = "0 0 0 2px #004c8640"}
                            onBlur={(e) => e.target.style.boxShadow = "none"}
                        />
                        {errors.email && (
                            <span className="block text-red-500 dark:text-red-400 text-xs mt-1">
                        {errors.email}
                    </span>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex justify-end gap-2.5 pt-3 mt-2 border-t border-gray-100 dark:border-navy-700">

                        {/* Cancel */}
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isSubmitting}
                            className="px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer border border-gray-200 dark:border-navy-600 bg-gray-100 dark:bg-navy-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-navy-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Cancel
                        </button>

                        {/* Submit */}
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            style={{ background: "#004c86" }}
                            className="px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer border-none text-white transition-all duration-200 hover:-translate-y-px disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            onMouseEnter={(e) => e.currentTarget.style.background = "#003d6b"}
                            onMouseLeave={(e) => e.currentTarget.style.background = "#004c86"}
                        >
                            {/* Spinner while submitting */}
                            {isSubmitting && (
                                <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            )}
                            {isSubmitting
                                ? "Saving..."
                                : user ? " Save Changes" : " Add Recipient"
                            }
                        </button>

                    </div>
                </form>
            </div>
        </div>
    );
}
